// Process-wide cache for the volumetric modal-pressure sweep cube.
//
// Why this exists
// ---------------
// The volumetric field (|P(x, f)| over the whole voxel grid, swept across
// 20-300 Hz) is the heaviest thing the viz pipeline renders.  The position
// search browses up to ten candidate placements, and the result step then
// "reveals" the chosen one.  Without a cache the cube is recomputed from
// scratch on every candidate flip and again on reveal, even though the
// answer is often byte-identical.
//
// The crucial physics fact: the cube is driven by the SOURCES (speakers),
// the room geometry, the wall/floor materials, and the treatment panels.
// It does NOT depend on the listener position — the listener only samples
// the field.  So a seat-only search (only the listener moves between
// candidates) produces ONE cube shared by every candidate and by the final
// reveal.  A speaker / joint search produces one cube per distinct speaker
// layout, cached as the user browses.
//
// Keying on that field-determining signature (listener deliberately
// excluded) means:
//   - browsing a candidate warms its cube,
//   - revealing it is a cache hit (instant),
//   - the advanced 3D editor, hitting the same hook for the same scene,
//     reads the same cube — the cache is module-level so it survives the
//     guided -> advanced remount.
//
// Entries are bounded by a small LRU so a long speaker search can't grow
// memory without limit (each cube is a multi-MB uint8 payload).  Thin
// wrapper around the generic createFetchCache — see that file for the
// shared Map/inflight/eviction implementation.

import type { ModalVolumeSweepResponse, ModalPipeline } from './adminTypes';
import { createFetchCache } from './createFetchCache';

// Each cube is a uint8 field of (F · nx · ny · nz) bytes carried as base64.
// A typical room is a few MB; cap the cache so a 10-candidate speaker sweep
// (× before/after) can't pin hundreds of MB.  LRU eviction keeps the most
// recently viewed candidates warm.  16, not the other caches' 24 — cubes
// are heavier than raytrace/speaker-sweep responses.
const MAX_ENTRIES = 16;

const cache = createFetchCache<string, ModalVolumeSweepResponse>({
  sig: (s) => s,
  maxEntries: MAX_ENTRIES,
});

export interface VolumetricSweepParams {
  f_min: number;
  f_max: number;
  df: number;
  fmax?: number;
}

// The ONE sweep band every volumetric request uses: df=2 Hz → 141 samples
// across 20-300 Hz.  Kept here (rather than in useModalViz) so the lazy
// per-candidate fetch and the gallery's eager preloader compute the cache
// signature from byte-identical params — a preloaded cube must key-match the
// reveal fetch or the "instant on flip" guarantee breaks.
export const VOL_SWEEP: VolumetricSweepParams = { f_min: 20, f_max: 300, df: 2 };

/** The minimal shape of a scene payload this cache needs to key on — a
 *  structural subset of `docToScenePayload`'s return so callers can pass
 *  that straight through. */
interface VolumetricPayloadLike {
  Lx: number; Ly: number; H: number;
  speakers: unknown;
  panels: unknown;
  furniture?: unknown;
  floor_material?: unknown;
  wall_material?: unknown;
  room_shape?: unknown;
  footprint_vertices?: unknown;
  wall_labels?: unknown;
  wings?: unknown;
  ceiling_profile?: unknown;
}

/** Field-determining signature for one volumetric request.  Listener
 *  position is intentionally omitted — see the file header for why. */
export function volumetricSig(
  payload: VolumetricPayloadLike,
  sweep: VolumetricSweepParams,
  pipeline: ModalPipeline | undefined,
): string {
  return JSON.stringify({
    Lx: payload.Lx, Ly: payload.Ly, H: payload.H,
    speakers: payload.speakers,
    panels: payload.panels,
    // Furniture changes the eigensolve (air displacement) + absorption, so it
    // must be part of the field cache key — otherwise moving furniture returns
    // the stale cached cube.
    furniture: payload.furniture ?? null,
    floor: payload.floor_material ?? null,
    wall: payload.wall_material ?? null,
    room_shape: payload.room_shape ?? null,
    footprint: payload.footprint_vertices ?? null,
    wall_labels: payload.wall_labels ?? null,
    wings: payload.wings ?? null,
    ceiling: payload.ceiling_profile ?? null,
    sweep: [sweep.f_min, sweep.f_max, sweep.df, sweep.fmax ?? null],
    pipeline: pipeline ?? null,
  });
}

/** Synchronous cache read.  Bumps the entry to most-recently-used so the
 *  LRU keeps the cubes the user is actively flipping between. */
export function getCachedVolumetric(sig: string): ModalVolumeSweepResponse | null {
  return cache.getCached(sig) ?? null;
}

/** Read-through fetch with in-flight dedup.  `fetcher` runs the actual
 *  network request and is only invoked on a cold miss; concurrent callers
 *  for the same signature share the one promise (so a candidate flip that
 *  re-enters while the cube is still loading doesn't double-submit). */
export async function fetchVolumetric(
  sig: string,
  fetcher: () => Promise<ModalVolumeSweepResponse>,
): Promise<ModalVolumeSweepResponse> {
  return cache.fetch(sig, fetcher);
}

/** Drop everything — use on doc reset / sign-out, mirroring
 *  `clearPreWarmInflight`. */
export function clearVolumetricCache(): void {
  cache.clear();
}

/** Land a precomputed cube so the next `fetchVolumetric` for this sig is
 *  a hit. Signature must be `volumetricSig(payload, VOL_SWEEP, pipeline)`
 *  with the same payload the live fetch will build (see applyTemplateDetail). */
export function primeCachedVolumetric(sig: string, resp: ModalVolumeSweepResponse): void {
  cache.prime(sig, resp);
}
